import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Shield, CheckCircle, AlertCircle, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function AdminSetup() {
  const { user, isAuthenticated, isAdmin, rolesLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  const [targetEmail, setTargetEmail] = useState('');

  const grantAdminAccess = async () => {
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only admins can grant admin access',
        variant: 'destructive',
      });
      return;
    }

    if (!targetEmail.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter an email address',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      // First, find the user by email
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('email', targetEmail.trim())
        .maybeSingle();

      if (userError || !userData) {
        toast({
          title: 'User Not Found',
          description: 'No user found with that email address',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      const { error } = await supabase
        .from('user_roles')
        .insert({
          user_id: userData.user_id,
          role: 'admin'
        });

      if (error) {
        // Check if already exists
        if (error.code === '23505') {
          toast({
            title: 'Info',
            description: 'This user already has admin access',
          });
          setLoading(false);
          return;
        }
        throw error;
      }

      toast({
        title: 'Success!',
        description: `Admin access granted to ${targetEmail}`,
      });

      setTargetEmail('');
      setSetupComplete(true);
      
      setTimeout(() => {
        setSetupComplete(false);
      }, 3000);

    } catch (error: any) {
      console.error('Error granting admin access:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to grant admin access',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (rolesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>
              You must be logged in to set up admin access
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/auth')} className="w-full">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <Lock className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              Only admins can access this page
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          {setupComplete ? (
            <>
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <CardTitle>Admin Access Granted!</CardTitle>
              <CardDescription>
                The user now has admin privileges
              </CardDescription>
            </>
          ) : (
            <>
              <Shield className="h-12 w-12 mx-auto mb-4 text-primary" />
              <CardTitle>Grant Admin Access</CardTitle>
              <CardDescription>
                Add admin privileges to a user account
              </CardDescription>
            </>
          )}
        </CardHeader>

        {!setupComplete && (
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">User Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={targetEmail}
                onChange={(e) => setTargetEmail(e.target.value)}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                Enter the email address of the user you want to grant admin access to
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Admin privileges include:</p>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Review discovered chatbots</li>
                <li>Approve or reject chatbots</li>
                <li>Manage the chatbot catalog</li>
                <li>Grant admin access to others</li>
              </ul>
            </div>

            <Button
              onClick={grantAdminAccess}
              disabled={loading || !targetEmail.trim()}
              className="w-full"
              size="lg"
            >
              {loading ? 'Granting Access...' : 'Grant Admin Access'}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              ⚠️ Only grant admin access to trusted users
            </p>
          </CardContent>
        )}
      </Card>
    </div>
  );
}